import { useEffect, useState } from "react";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Theme, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { restrictionOptions } from "../../../../../../utils/Constant";
import CancelIcon from "@mui/icons-material/Cancel";
import { updateInspectorRequirementsRequest } from "../../../../../../utils/APICalls";
import { useDispatch, useSelector } from "react-redux";
import { getProcessesProcedureRequest } from "../../../../../../redux/clients/slice";
import Loading from "../../../../../../components/loading/loading";
import { getInsuranceTypesRequest } from "../../../../../../redux/inspection-firms/slice";

function getStyles(name: string, personName: readonly string[], theme: Theme) {
  return {
    fontWeight: personName.includes(name)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
  clientId: string;
};

export default function EditInspectorRequirement({
  showDialog,
  closeDialog,
  data,
  clientId,
}: props) {
  const theme = useTheme();
  const [restrictions, setRestrictions] = useState<any[]>([]);
  const [backgroundCheck, setBackgroundCheck] = useState("false");
  const [insurances, setInsurances] = useState<string[]>([]);
  const [submitCount, setSubmitCount] = useState(0);
  const [showLoading, setShowLoading] = useState(false);

  const dispatch = useDispatch();
  const { loading, insuranceTypes } = useSelector(
    (state: any) => state.inspectionFirms
  );

  useEffect(() => {
    if (insuranceTypes.length === 0) dispatch(getInsuranceTypesRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
    if (data) {
      if (data.hasOwnProperty("backgroundCheckDone")) {
        setRestrictions((prev) => [
          ...prev,
          { type: "Background Check Completed" },
        ]);
        setBackgroundCheck(
          data.backgroundCheckDone === true ? "true" : "false"
        );
      }

      if (data.insurances?.length > 0) {
        setRestrictions((prev) => [...prev, { type: "Insurance Required" }]);
        setInsurances(data.insurances);
      }
    }
  }, [data]);

  const handleRestrictionTypeChange = (e: SelectChangeEvent, index: number) => {
    const allRestrictions = [...restrictions];
    allRestrictions[index].type = e.target.value;
    setRestrictions(allRestrictions);
  };

  const handleBackgroundCheckChange = (e: SelectChangeEvent) => {
    setBackgroundCheck(e.target.value);
  };

  const handleInsuranceChange = (
    event: SelectChangeEvent<typeof insurances>
  ) => {
    const { value }: any = event.target;
    setInsurances(value);
  };

  const handleInsuranceDelete = (option: string) => {
    setInsurances((prev) => prev.filter((value) => value !== option));
  };

  const addRestictrionClick = () => {
    setRestrictions((prev) => [...prev, { type: "" }]);
  };

  const checkDuplicate = (option: string) => {
    let isSelected = false;
    restrictions.forEach((restriction) => {
      if (restriction.type === option) {
        isSelected = true;
      }
    });
    return isSelected;
  };

  const deleteRestictrionClick = (index: number) => {
    const allRestrictions = [...restrictions];
    allRestrictions.splice(index, 1);
    setRestrictions(allRestrictions);
  };

  const onSubmitClick = () => {
    setSubmitCount(submitCount + 1);
    if (isRestricionTypeEmpty()) {
      return;
    }
    const backgroundCheckFilter = restrictions.filter(
      (restriction) => restriction.type === "Background Check Completed"
    );
    const insuranceFilter = restrictions.filter(
      (restriction) => restriction.type === "Insurance Required"
    );
    if (
      insuranceFilter &&
      insuranceFilter.length > 0 &&
      insurances.length === 0
    ) {
      return;
    }

    const payload: any = {};
    if (backgroundCheckFilter.length > 0) {
      payload.backgroundCheckDone = backgroundCheck === "true" ? true : false;
    }
    if (insuranceFilter.length > 0) {
      payload.insurances = insurances;
    }
    updateInspectorRequirements(payload);
  };

  const isRestricionTypeEmpty = () => {
    let isEmpty = false;
    restrictions.forEach((restriction) => {
      if (!restriction.type) {
        isEmpty = true;
      }
    });
    return isEmpty;
  };

  const updateInspectorRequirements = async (payload: any) => {
    setShowLoading(true);
    const response = await updateInspectorRequirementsRequest(
      clientId,
      payload
    );
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getProcessesProcedureRequest({ id: clientId }));
      closeDialog();
    }
  };

  const getInsuranceDisplayName = (insuranceType: string) => {
    const filterUser = insuranceTypes?.filter(
      (insurance: any) => insurance.insuranceType === insuranceType
    );
    return filterUser && filterUser.length > 0 && filterUser[0].displayName;
  };

  return (
    <DialogContainer
      title="Client - Processes & Procedures - Inspector Requirements"
      closeDialog={closeDialog}
      showDialog={showDialog}
      onSubmit={onSubmitClick}
    >
      <Box py={2.5} display={"flex"}>
        <Typography className="model-label-txt">
          Standard Restrictions
        </Typography>
        <Box sx={{ width: "calc(100% - 20px)" }}>
          {restrictions?.length === 0 && (
            <Typography>No Restrictions</Typography>
          )}
          {restrictions?.map((restriction, index) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: index === 0 ? 0 : 2,
                position: "relative",
              }}
            >
              <FormControl
                sx={{ width: 450 }}
                error={!restriction.type && submitCount > 0}
              >
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={restriction.type}
                  onChange={(e) => handleRestrictionTypeChange(e, index)}
                >
                  {restrictionOptions.map((option) => (
                    <MenuItem value={option} disabled={checkDuplicate(option)}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {!restriction.type && submitCount > 0 && (
                  <Typography
                    sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                    color="error"
                  >
                    This field can't be empty
                  </Typography>
                )}
              </FormControl>
              {restriction.type === "Background Check Completed" && (
                <>
                  <FormControl sx={{ width: 450, mx: 2 }}>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={backgroundCheck}
                      onChange={handleBackgroundCheckChange}
                    >
                      <MenuItem value={"true"}>True</MenuItem>
                      <MenuItem value={"false"}>False</MenuItem>
                    </Select>
                    {!backgroundCheck && submitCount > 0 && (
                      <Typography
                        sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                        color="error"
                      >
                        This field can't be empty
                      </Typography>
                    )}
                  </FormControl>
                </>
              )}
              {restriction.type === "Insurance Required" && (
                <>
                  <FormControl
                    sx={{ width: 450, mx: 2 }}
                    error={
                      insurances && insurances.length === 0 && submitCount > 0
                    }
                  >
                    <Select
                      labelId="demo-multiple-chip-label"
                      id="demo-multiple-chip"
                      multiple
                      value={insurances}
                      onChange={handleInsuranceChange}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip
                              color="primary"
                              variant="outlined"
                              onDelete={() => handleInsuranceDelete(value)}
                              key={value}
                              label={getInsuranceDisplayName(value)}
                              deleteIcon={
                                <IconButton
                                  onMouseDown={(e) => e.stopPropagation()}
                                  size="small"
                                  sx={{ padding: 0.5 }}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              }
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {insuranceTypes.map((option: any) => (
                        <MenuItem
                          key={option.insuranceType}
                          value={option.insuranceType}
                          style={getStyles(
                            option.insuranceType,
                            insurances,
                            theme
                          )}
                        >
                          {option.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                    {insurances &&
                      insurances.length === 0 &&
                      submitCount > 0 && (
                        <Typography
                          sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                          color="error"
                        >
                          This field can't be empty
                        </Typography>
                      )}
                  </FormControl>
                </>
              )}
              <IconButton
                sx={{ position: "absolute", right: 0 }}
                aria-label="delete"
                onClick={() => deleteRestictrionClick(index)}
              >
                <DeleteOutlineOutlinedIcon style={{ color: "#D32F2F" }} />
              </IconButton>
            </Box>
          ))}

          {restrictions?.length < 2 && (
            <Box mt={2}>
              <Button
                sx={{ fontWeight: "510" }}
                variant="text"
                startIcon={<AddIcon />}
                onClick={addRestictrionClick}
              >
                ADD RESTRICTION
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      {(loading || showLoading) && <Loading />}
    </DialogContainer>
  );
}
